// frontend/src/theme.js
//
// The single source of truth for every colour, typeface and scale in the app.
//
// Before this file the frontend styled itself with 1,686 literal colours spread
// across 33 files — 109 distinct hex values and 60 distinct rgba() strings for
// what is really a six-colour brand. Changing the gold meant a hundred-and-fifty
// find-and-replaces and a prayer, and near-identical shades (#faf8f5, #faf7f4,
// #f9f6f1) had drifted apart in different files with nobody deciding they should
// differ. Components now reference tokens; literals live here and nowhere else.
//
// Why plain JS objects rather than CSS classes: the app styles itself with inline
// `style={{}}` objects, so a token has to be a JavaScript value. It also has to
// survive being handed to a lucide-react icon as `color=`, which lands on the SVG
// `stroke` presentation attribute — and `var()` is not substituted there. A JS
// token works in every position; a CSS custom property does not. The custom
// properties are still emitted (see `injectCssVariables` below) for the handful
// of places that are genuinely CSS: the <style> template blocks with @keyframes
// and @media in them, and anything added later.
//
// The values started as exactly what was in the code — extracting them was a
// rename, not a redesign. They were then consolidated in a second, deliberate
// pass: 45 of the 107 turned out to be another colour written slightly
// differently, and were merged into it. See the note at the bottom for the rule
// that decided which, and for the two that were left alone.

// ─────────────────────────────────────────────────────────────────────────────
// Typefaces
// ─────────────────────────────────────────────────────────────────────────────
//
// Both faces are self-hosted from /public/fonts (see public/fonts/fonts.css) so
// that rendering a page makes no third-party request.
//
// These are NOT the original two faces. The app shipped with Cormorant Garamond
// and Montserrat, and neither of them can draw a Greek character — not "the
// subset was not installed", but no Greek glyphs exist in the fonts at all
// (checked against the Google Fonts API: latin, latin-ext, cyrillic, vietnamese
// and nothing else). Since index.html declares lang="el" and Greek is the
// default language for every new visitor, the *primary* audience was reading the
// whole site in the browser's fallback serif while the Latin text beside it
// rendered in the brand face.
//
// EB Garamond and Manrope replace them because each covers latin, greek and
// cyrillic from one drawing. That matters beyond the immediate bug: the Greek
// copy is full of Latin words that Cypriots genuinely use ("premium Remy
// clip-in", "smart locker"), so a mixed-script line is the normal case here, not
// the exception — the two scripts have to come from the same typeface or every
// second sentence looks broken. Cyrillic comes along for free, which is what a
// Russian locale for Limassol will need.
//
// EB Garamond was chosen over the alternatives because it is a Garamond, like
// the face it replaces, so the Latin look barely moves. Its weight range starts
// at 400 where Cormorant had a 300 — requests for 300 in the display face are
// matched up to 400 by the CSS font-matching algorithm. That is accepted, not
// an oversight.
//
// The generic at the end of each stack is still load-bearing: 108 of the 361
// font declarations in the app were written as a bare `fontFamily: 'Montserrat'`
// with no generic after it.

const font = {
  display: "'EB Garamond', Georgia, 'Times New Roman', serif",
  body: "'Manrope', 'Helvetica Neue', Arial, sans-serif",
  mono: 'monospace',
};

// ─────────────────────────────────────────────────────────────────────────────
// Primitives — the raw palette
// ─────────────────────────────────────────────────────────────────────────────

const palette = {
  // Ink: the near-blacks. `ink` is both the body text colour and the dark
  // section background, which is why it is named for the pigment rather than
  // for one of its two jobs.
  inkDeep: '#0a0a0a', // landing hero
  ink: '#1a1a1a', // primary text, dark surfaces
  ink800: '#2a2a2a',
  ink700: '#3a3a3a',
  ink600: '#4a4a4a',
  espresso: '#1a0f08', // warm black, used behind gold CTAs
  espressoDeep: '#2d1c10',

  // Warm greys — the public site's text ramp. The `?a?a?a` shape is deliberate
  // in the original code: they are very slightly warm, not neutral.
  grey600: '#5a5a5a', // secondary body text
  grey550: '#6a6a6a',
  grey500: '#7a7a7a',
  grey450: '#8a8a8a',
  grey400: '#9a9a9a', // muted / captions
  grey300: '#b8b8b8',
  grey240: '#c9c9c9',
  grey120: '#e8e8e8',
  grey100: '#f0f0f0',
  // Two cool greys that only the admin panel uses.
  slate400: '#aaaaaa',

  white: '#ffffff',

  // Cream and sand — the light surfaces and the hairline borders between them.
  // There were fifteen of these, within a couple of points of each other. Three
  // is what the app actually distinguishes.
  cream: '#faf8f5', // the page background
  creamDeep: '#f5f0e8',
  creamBone: '#e8e0d8',

  sand: '#e8ddd0', // the default border colour, used 135 times
  sandMid: '#d4c9bb',
  sandDeep: '#d4c0a0',
  sandTan: '#e8d0b4',
  taupe: '#b0a090',
  taupeSoft: '#c0b090',

  // Gold — the one metallic accent. `gold` is the brand colour; `goldInk` is the
  // darkened version used when gold has to be legible as text on cream, which
  // #c9a96e is not (2.2:1 against #faf8f5).
  gold: '#c9a96e',
  goldDeep: '#b8944f', // the far stop of every gold gradient
  goldBrass: '#b8860b',
  goldInk: '#7a5a1a',
  goldInkAlt: '#8a5a1a',
  goldOlive: '#7a6a4a',
  goldMoss: '#5a4a2a',
  goldPale: '#e8d5a8',
  goldWash: '#fff8ec',

  // Status. Each of these travels as a background/foreground pair — see
  // `state` below, which is how they should be referenced.
  amber: '#b0791a',
  amberDeep: '#c47a20',
  amberWash: '#fef3cd',

  green: '#2d7a4a',
  greenDeep: '#1a6b3a',
  greenLight: '#4a9a4a',
  greenSoft: '#5a8a5a',
  greenWash: '#d4edda',
  greenWashSoft: '#eaf6ee',

  red: '#e04a2f', // the brand's error/terracotta
  redBrick: '#c0392b',
  redDark: '#8a1a1a',
  redSoft: '#b45252',
  redWash: '#f8d7da',
  redWashPale: '#fff5f5',
  redWashWarm: '#fdeceb',
  redBorder: '#e0b4b4',
  redBorderRose: '#d8c4c4',
  redBorderPeach: '#e8c4b8',
  redBorderBlush: '#ffcccc',

  blue: '#1a4a8a',
  blueWash: '#cce5ff',
  blueWashSoft: '#e8f0fb',

  purple: '#6a1a8a',
  purpleWash: '#f3e5f5',
  purpleBorder: '#d9bde3',
};

// ─────────────────────────────────────────────────────────────────────────────
// Translucency
// ─────────────────────────────────────────────────────────────────────────────
//
// 60 distinct rgba() strings existed, which is 60 chances to write
// rgba(201,169,110,…) and mean "the gold" without anything saying so. These
// helpers keep the base colour attached to its name: alpha.gold(0.14) is
// obviously the accent at 14%, rgba(201,169,110,0.14) is a magic number.

const rgba = (r, g, b) => (a) => `rgba(${r},${g},${b},${a})`;

const alpha = {
  black: rgba(0, 0, 0),
  white: rgba(255, 255, 255),
  gold: rgba(201, 169, 110), // palette.gold
  ink: rgba(26, 26, 26), // palette.ink
  inkDeep: rgba(10, 10, 10), // palette.inkDeep
};

// ─────────────────────────────────────────────────────────────────────────────
// Semantic layer — prefer these in new code
// ─────────────────────────────────────────────────────────────────────────────
//
// The primitives say what a colour *is*; these say what it is *for*. A colour
// that changes role should change token here rather than being re-picked in a
// component.

const text = {
  primary: palette.ink,
  secondary: palette.grey600,
  muted: palette.grey400,
  faint: palette.grey500,
  onDark: palette.white,
  onDarkMuted: alpha.white(0.6),
  accent: palette.goldInk, // gold that has to be readable on cream
  link: palette.goldInk,
};

const surface = {
  page: palette.cream,
  card: palette.white,
  sunken: palette.creamDeep,
  raised: palette.white,
  dark: palette.ink,
  darker: palette.inkDeep,
  warm: palette.espresso,
};

const border = {
  soft: palette.creamBone,
  default: palette.sand,
  strong: palette.sandMid,
  onDark: alpha.white(0.22),
  accent: palette.gold,
  focus: palette.gold,
};

const accent = {
  base: palette.gold,
  deep: palette.goldDeep,
  ink: palette.goldInk,
  wash: alpha.gold(0.1),
  gradient: `linear-gradient(135deg, ${palette.gold}, ${palette.goldDeep})`,
};

// Background / foreground pairs for a status pill. The same seven pairs were
// written out by hand in six files — MyRentals, RentalDetail and four admin
// screens — so "what does a late rental look like" had six answers that merely
// happened to agree. The `bg` / `color` key names match the call sites, which is
// why they are not `bg` / `fg`.
//
// Which status maps to which pair stays in the page, deliberately: that a
// rental going `disputed` should read as a dispute is domain knowledge, and the
// screens do not all carry the same set of statuses.
const state = {
  pending: { bg: palette.amberWash, color: palette.amber },
  success: { bg: palette.greenWash, color: palette.greenDeep },
  info: { bg: palette.blueWash, color: palette.blue },
  neutral: { bg: palette.grey120, color: palette.grey600 },
  danger: { bg: palette.redWash, color: palette.redDark },
  muted: { bg: palette.grey100, color: palette.grey450 },
  dispute: { bg: palette.purpleWash, color: palette.purple },
  // Written off. Deliberately the heaviest of the set rather than another shade
  // of red: 'late' is already danger, and these two sit next to each other in
  // the same list, where "still chasing it" and "gone, closed, paid for" must
  // not read as the same state at a glance.
  lost: { bg: palette.grey120, color: palette.ink },
};

// ─────────────────────────────────────────────────────────────────────────────
// Non-colour scales
// ─────────────────────────────────────────────────────────────────────────────
//
// These describe what the app already does — the sizes below are the ones in
// use, not a fresh modular scale. Rounding the existing values onto a clean
// 1.25 ratio would move type on every screen, so it is deliberately a separate
// decision from extracting the tokens.

const size = {
  hero: 64,
  display: 48,
  h1: 40,
  h2: 32,
  h3: 26,
  h4: 22,
  lead: 18,
  body: 15,
  bodySm: 14,
  label: 13,
  caption: 12,
  micro: 11,
  tiny: 10,
};

const weight = { light: 300, regular: 400, medium: 500, semibold: 600 };

const tracking = {
  tight: '0.04em',
  normal: '0.06em',
  wide: '0.08em',
  wider: '0.1em',
  widest: '0.15em',
  ultra: '0.25em',
};

const leading = { tight: 1.2, snug: 1.4, normal: 1.6, relaxed: 1.8 };

// A 4px base. 24 is the standard gutter, 80 the standard section padding.
const space = {
  xs: 4, sm: 8, md: 12, base: 16, lg: 24, xl: 32, '2xl': 48, '3xl': 64, '4xl': 80, '5xl': 120,
};

const radius = { sharp: 2, base: 4, md: 6, lg: 8, xl: 12, pill: 100 };

const shadow = {
  none: 'none',
  sm: `0 2px 8px ${alpha.black(0.04)}`,
  base: `0 2px 20px ${alpha.black(0.06)}`,
  md: `0 4px 20px ${alpha.black(0.08)}`,
  lg: `0 8px 28px ${alpha.black(0.12)}`,
  xl: `0 24px 64px ${alpha.black(0.18)}`,
  overlay: `0 24px 80px ${alpha.black(0.45)}`,
  accent: `0 8px 40px ${alpha.gold(0.25)}`,
};

// Slow and soft on purpose. The brand's motion is 600–900ms with a gentle
// ease-out; anything springy or under 300ms reads as a web app rather than a
// luxury one. Motion.js and the 3D hero both key off these.
const motion = {
  fast: 200,
  base: 400,
  slow: 700,
  slower: 900,
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
  easeInOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
};

const z = { base: 0, raised: 10, sticky: 100, nav: 500, overlay: 1000, modal: 2000, toast: 3000 };

// ─────────────────────────────────────────────────────────────────────────────
// CSS custom properties
// ─────────────────────────────────────────────────────────────────────────────
//
// Emitted from the same objects above rather than written out a second time —
// two hand-maintained copies of a palette is the problem this file exists to
// remove. Injected once at import, before React mounts, so the <style> block is
// in the document for any CSS that wants `var(--gg-gold)`. Only the flat colour
// primitives are exported this way; the alpha helpers are functions and the
// pairs are structural.

const cssVariableText = () => {
  const decl = [];
  for (const [k, v] of Object.entries(palette)) {
    decl.push(`  --gg-${k.replace(/([A-Z0-9]+)/g, (m) => `-${m.toLowerCase()}`)}: ${v};`);
  }
  decl.push(`  --gg-font-display: ${font.display};`);
  decl.push(`  --gg-font-body: ${font.body};`);
  decl.push(`  --gg-font-mono: ${font.mono};`);
  for (const [k, v] of Object.entries(radius)) decl.push(`  --gg-radius-${k}: ${v}px;`);
  for (const [k, v] of Object.entries(motion)) {
    decl.push(`  --gg-motion-${k}: ${typeof v === 'number' ? `${v}ms` : v};`);
  }
  return `:root {\n${decl.join('\n')}\n}`;
};

export const injectCssVariables = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('gg-tokens')) return;
  const el = document.createElement('style');
  el.id = 'gg-tokens';
  el.textContent = cssVariableText();
  // First child of <head> so that anything later can override a token.
  document.head.insertBefore(el, document.head.firstChild);
};

injectCssVariables();

// ─────────────────────────────────────────────────────────────────────────────

const T = {
  font,
  ...palette,
  alpha,
  text,
  surface,
  border,
  accent,
  state,
  size,
  weight,
  tracking,
  leading,
  space,
  radius,
  shadow,
  motion,
  z,
};

export { palette, alpha, font, text, surface, border, accent, state, size, space, radius, shadow, motion, cssVariableText };
export default T;

// ─────────────────────────────────────────────────────────────────────────────
// How the palette was consolidated
// ─────────────────────────────────────────────────────────────────────────────
//
// 107 colours went in, 62 came out. The merges were decided by measurement
// rather than by eye, because eyeballing hex codes is how there came to be four
// page creams in the first place. Three rules, each of which vetoed merges the
// one before it would have allowed:
//
//   1. CIEDE2000 distance, complete linkage. Every member of a group has to be
//      within the threshold of every other member. Single linkage chains — with
//      eleven near-whites in the palette it walked #c8c8c8 all the way to
//      #ffffff, a ΔE of 12.
//
//   2. Two thresholds. ΔE is measured on small patches, and the eye is much
//      better at telling two full-viewport fields apart than two swatches, so
//      the colours that fill a page — white, cream, creamDeep, ink, inkDeep,
//      espresso — and any pair the semantic layer already calls different roles
//      are held to 2.3, the just-noticeable difference. Everything else gets 5.
//
//   3. Family. Distance carries no meaning with it: #fff5f5, the tint behind a
//      warning, is close enough to #f0f0f0, a disabled grey, to merge — and
//      merging them turns every red alert panel grey. A merge has to stay inside
//      one hue family, which is what the token names encode.
//
// Then every merge touching a foreground was contrast-checked against the
// background it actually sits on, and every border against the surface behind
// it, so that nothing became illegible or invisible. Two merges were refused on
// that basis and the colours kept:
//
//   slate400 #aaaaaa ⇸ grey300   2.19:1 -> 1.87:1   Landing newsletter disclaimer
//   taupeSoft #c0b090 ⇸ sandDeep 2.01:1 -> 1.67:1   admin Lockers map hint
//
// Both already fail WCAG AA at 11px and 10px respectively. They are a debt to
// pay by darkening the text, not by merging it into something lighter.
